import styled from 'styled-components';

import { useTranslation } from 'react-i18next';
import { themeGet } from '@styled-system/theme-get';
import { Tooltip } from '@radix-ui/themes';
import Text from '../../../../shared/Text';
import Button, { ButtonBase } from '../../../../shared/Button';
import { usePlans } from '../../../../lib/hooks/usePlans';
import { useCampaignContext } from '../../contexts/CampaignContext.jsx';
import {
  REMINDER,
  MESSAGE,
  REVIEW,
  DRAFT,
  CHOOSE,
  LIVE,
  REVIEW_WITHOUT_NOTIFICATION,
} from '../../utils/constants';

export const NotificationsModal = ({
  messageForm,
  onClose,
  children,
  setStep,
  step,
  setOpenPostNow,
  post,
  title,
}) => {
  const { t } = useTranslation('postNotifications');
  const { campaign } = useCampaignContext();
  const isDeleted = !!campaign?.deletedAt;
  const canSave =
    !isDeleted &&
    ((step.currentStep === MESSAGE && post.status !== LIVE) ||
      (step.currentStep === REMINDER && !post.message?.reminderSent));

  return (
    <Wrapper>
      <Nav>
        <Text variant="h5">{title}</Text>
        {!step.singleStep &&
          !isDeleted &&
          ![REVIEW_WITHOUT_NOTIFICATION, CHOOSE].includes(step.currentStep) && (
            <Button
              margin="0 12px 0 auto"
              onClick={() => {
                if (step.currentStep === MESSAGE) {
                  messageForm.onSaveNotification({});
                }
                if (step.currentStep === REMINDER) {
                  messageForm.onSaveReminder({});
                }
                onClose();
              }}
              mode="outline"
              variant="black"
              width="auto"
            >
              <i
                className="fa-regular fa-floppy-disk"
                style={{ marginRight: '5px' }}
              ></i>
              {t('header.saveAndClose')}
            </Button>
          )}
        <Button
          onClick={() => onClose()}
          mode="text"
          variant="black"
          width="auto"
        >
          <i className="fa-solid fa-x"></i>
        </Button>
      </Nav>
      <Body>
        <Content>{children}</Content>
        <BodySpacing></BodySpacing>
      </Body>
      <Footer>
        {post.status === DRAFT ? (
          <DraftFooter
            currentStep={step.currentStep}
            setStep={setStep}
            previousStep={step.previousStep}
            messageForm={messageForm}
            singleStep={step.singleStep}
            setOpenPostNow={setOpenPostNow}
            onClose={onClose}
          />
        ) : (
          canSave && (
            <FooterButton
              onClick={() => {
                switch (step.currentStep) {
                  case MESSAGE:
                    messageForm.onSaveNotification({ showNotification: true });
                    onClose();
                    break;
                  case REMINDER:
                    messageForm.onSaveReminder({ showNotification: true });
                    onClose();
                    break;
                  default:
                    onClose();
                }
              }}
              mode="contained"
              variant="primary"
              marginLeft="auto"
            >
              {'Save Changes'}
            </FooterButton>
          )
        )}
      </Footer>
    </Wrapper>
  );
};

const ConditionalTooltip = ({ condition, content, children, style }) => {
  return condition ? (
    <div style={style}>
      <Tooltip content={content} side="top">
        <span>{children}</span>
      </Tooltip>
    </div>
  ) : (
    children
  );
};

const DraftFooter = ({
  currentStep,
  setStep,
  messageForm,
  singleStep,
  setOpenPostNow,
  onClose,
  previousStep,
}) => {
  const { t } = useTranslation('postNotifications');
  const { plans } = usePlans();
  const { campaign } = useCampaignContext();
  const canMakeCampaignLive = plans?.campaignInfo?.canMakeCampaignLive ?? true;
  const canCreateNewHireCampaign =
    plans?.campaignInfo?.canCreateNewHireCampaign ?? true;

  const isNewHireCampaign = campaign?.newHire;
  const isButtonEnabled =
    campaign?.status === 'active' ||
    (isNewHireCampaign ? canCreateNewHireCampaign : canMakeCampaignLive);

  const showTooltip = !isButtonEnabled;
  const tooltipMessage = isNewHireCampaign
    ? t('upgrade.newHireCampaignLimitReached')
    : t('upgrade.campaignLimitReached');

  if (currentStep === MESSAGE) {
    return (
      <>
        {!singleStep && (
          <FooterButton
            onClick={() =>
              setStep(prevState => ({
                ...prevState,
                currentStep: CHOOSE,
                previousStep: null,
              }))
            }
            mode="outline"
            variant="black"
          >
            {t('footer.previous')}
          </FooterButton>
        )}
        <FooterButton
          onClick={() => {
            if (singleStep) {
              setStep(prevState => ({
                ...prevState,
                currentStep: REVIEW,
                previousStep: MESSAGE,
              }));
            } else {
              setStep({ currentStep: REMINDER, previousStep: MESSAGE });
            }
            messageForm.onSaveNotification({});
          }}
          marginLeft="auto"
          mode="contained"
          variant="primary"
        >
          {singleStep ? 'Next: Review and Schedule' : t('footer.addReminder')}
        </FooterButton>
      </>
    );
  }

  if (currentStep === REMINDER) {
    return (
      <>
        {!singleStep && (
          <FooterButton
            onClick={() =>
              setStep(prevState => ({
                ...prevState,
                currentStep: MESSAGE,
                previousStep: CHOOSE,
              }))
            }
            mode="outline"
            variant="black"
          >
            {t('footer.previous')}
          </FooterButton>
        )}
        <FooterButton
          marginLeft="auto"
          onClick={() => {
            setStep(prevState => ({
              ...prevState,
              currentStep: REVIEW,
              previousStep: REMINDER,
            }));
            messageForm.onSaveReminder({});
          }}
          mode="contained"
          variant="primary"
        >
          Next: Review and Schedule
        </FooterButton>
      </>
    );
  }

  if (currentStep === REVIEW) {
    return (
      <>
        {previousStep && (
          <FooterButton
            onClick={() =>
              setStep(prevState => ({
                ...prevState,
                currentStep: prevState.previousStep,
                previousStep: REMINDER,
              }))
            }
            mode="outline"
            variant="black"
          >
            {t('footer.previous')}
          </FooterButton>
        )}
        <ConditionalTooltip
          condition={showTooltip}
          content={tooltipMessage}
          style={{ marginLeft: 'auto' }}
        >
          <FooterButton
            onClick={() => {
              setOpenPostNow(true);
            }}
            marginLeft={isButtonEnabled ? 'auto' : undefined}
            mode="outline"
            variant="black"
            disabled={!isButtonEnabled}
          >
            Post Now
          </FooterButton>
        </ConditionalTooltip>
        <ConditionalTooltip condition={showTooltip} content={tooltipMessage}>
          <FooterButton
            onClick={() => {
              messageForm.schedulePost();
              onClose();
            }}
            mode="contained"
            variant="primary"
            disabled={!isButtonEnabled}
          >
            Schedule
          </FooterButton>
        </ConditionalTooltip>
      </>
    );
  }

  if (currentStep === REVIEW_WITHOUT_NOTIFICATION) {
    return (
      <>
        <FooterButton
          onClick={() =>
            setStep(prevState => ({
              ...prevState,
              currentStep: CHOOSE,
              previousStep: null,
            }))
          }
          mode="outline"
          variant="black"
        >
          {t('footer.previous')}
        </FooterButton>
        <ConditionalTooltip
          condition={showTooltip}
          content={tooltipMessage}
          style={{ marginLeft: 'auto' }}
        >
          <FooterButton
            onClick={() => {
              messageForm.publishPostNow();
              onClose();
            }}
            marginLeft={isButtonEnabled ? 'auto' : undefined}
            mode="contained"
            variant="primary"
            disabled={!isButtonEnabled}
          >
            Make Live Now
          </FooterButton>
        </ConditionalTooltip>
      </>
    );
  }

  return null;
};

const BodySpacing = styled.div`
  height: 8px;
  width: 100%;
`;

const FooterButton = styled(ButtonBase)`
  padding: 15px 16px;
  width: auto;
  height: auto;
`;

const Footer = styled.div`
  height: 74px;
  background: ${themeGet('colors.white')};
  border-top: 1px solid ${themeGet('colors.black.10')};
  padding: 12px 24px;
  border-radius: 0 0px 8px 8px;
  display: flex;
  gap: 12px;
`;

const Nav = styled.nav`
  display: flex;
  height: 74px;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid ${themeGet('colors.black.10')};
  align-items: center;
  background-color: ${themeGet('colors.white')};
  border-radius: 8px 8px 0 0;
`;

const Wrapper = styled.div`
  background-color: ${themeGet('colors.background')};
  border-radius: 8px;
  max-width: 1360px;
  margin: 50px auto;
  display: flex;
  flex-direction: column;
  height: 90%;
  max-height: 982px;
`;

const Body = styled.div`
  padding: 24px;
  background-color: ${themeGet('colors.background')};
  height: 100%;
  overflow: auto;
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
`;
